import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useIsOnline } from "src/hooks";
import { useDispatch } from "src/redux/hooks";
import { setPendingRequests } from "src/redux/slices/pendingRequestsSlice";
/**
 * Custom hook to handle desktop database synchronization.
 *
 * @returns An object containing the synchronization state, progress, reload iterator, and loading visibility status.
 */
export const useDesktopDatabaseSync = () => {
  const [reloadIterator, setReloadIterator] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [progress, setProgress] = useState(0);
  const isOnline = useIsOnline();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { logbookId, idType, lbeidOrPartId } = useParams();

  useEffect(() => {
    if (window.api) {
      window.api.onPendingChange(pending => {
        dispatch(setPendingRequests(pending));
      });

      if (!isOnline) {
        window.api.updatePending();
      }
    }

    return () => {
      if (window.api) {
        window.api.onPendingChange(null);
      }
    };
  }, [dispatch, isOnline]);

  useEffect(() => {
    if (window?.api) {
      window.api.onSyncBegin(() => {
        setIsSyncing(true);
      });
      window.api.onSyncChange((started, completed) => {
        setProgress(Number(((completed / started) * 100).toFixed(0)));
      });
      window.api.onSyncEnd(() => {
        setIsSyncing(false);
        setProgress(0);
        if (lbeidOrPartId?.includes("new=")) {
          navigate(`/logbook/${logbookId}/${idType}`);
        }
        setReloadIterator(prev => prev + 1);
      });
    }

    return () => {
      if (window?.api) {
        window.api.onSyncBegin(null);
        window.api.onSyncChange(null);
        window.api.onSyncEnd(null);
      }
    };
  }, [idType, lbeidOrPartId, logbookId, navigate]);

  return {
    isSyncing,
    progress,
    reloadIterator,
    isLoadingVisible: isSyncing && isOnline,
  };
};
